import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { roundFloat } from "../utils/round-float";
import useToast from "../hooks/useToast";

type Props = {
  children: React.ReactElement;
};

const SoundContainer = (props: Props) => {
  const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
  const audioCtxRef = useRef<AudioContext | null>(null);
  const userSettings = useSelector((state: any) => state.userSettings);
  const isPlayable = useSelector((state: any) => state.audioPlayable);
  const isMapMode = useSelector((state: any) => state.mapMode?.isMapMode || false);
  const gainNodeRef = useRef<GainNode | null>(null);
  const bufferSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const AudioBufferState = useRef<any>();
  const IsFirst = useRef(true);
  const openToast = useToast();
  const isPlayableRef = useRef(isPlayable);
  const currentRoute = useRef<string>("");
  const currentMapMode = useRef<boolean>(false);
  const currentSoundFile = useRef<string>("");
  const isInitializing = useRef<boolean>(false);
  const hasUserInteracted = useRef<boolean>(false);
  const initialDelayTimer = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    isPlayableRef.current = isPlayable;
  }, [isPlayable]);

  // Initialize audio context and nodes
  const initializeAudioContext = useCallback(() => {
    if (audioCtxRef.current) {
      // Only close if not already closed, and catch promise rejections to avoid unhandled errors
      if (audioCtxRef.current.state !== 'closed') {
        const closePromise = audioCtxRef.current.close();
        if (closePromise && typeof closePromise.catch === 'function') {
          closePromise.catch(() => {});
        }
      }
    }
    
    audioCtxRef.current = new AudioContext();
    gainNodeRef.current = audioCtxRef.current.createGain();
    bufferSourceRef.current = audioCtxRef.current.createBufferSource();
    
    return audioCtxRef.current;
  }, []);

  // Stop current audio
  const stopCurrentAudio = useCallback(() => {
    if (bufferSourceRef.current) {
      try {
        bufferSourceRef.current.stop();
        bufferSourceRef.current.disconnect();
      } catch (e) {
        // Ignore errors if already stopped
      }
      bufferSourceRef.current = null;
    }
    
    if (audioCtxRef.current && audioCtxRef.current.state === "running") {
      try {
        audioCtxRef.current.suspend();
      } catch (e) {
        // Ignore errors if context is already closed
      }
    }
  }, []);

  // Play sound with proper audio management
  const playSound = useCallback((audioBuffer: any) => {
    if (!audioCtxRef.current || !gainNodeRef.current) {
      return;
    }
    
    // Don't play if user hasn't interacted yet
    if (!hasUserInteracted.current) {
      return;
    }
    
    // Create new buffer source for each play
    bufferSourceRef.current = audioCtxRef.current.createBufferSource();
    
    const volumeValue = roundFloat(userSettings?.backgroundMusicVolume / 10, 1);
    gainNodeRef.current.gain.value = parseFloat(typeof volumeValue === 'string' ? volumeValue : '0');
    bufferSourceRef.current.buffer = audioBuffer;
    bufferSourceRef.current.connect(gainNodeRef.current);
    gainNodeRef.current.connect(audioCtxRef.current.destination);
    bufferSourceRef.current.loop = true;
    
    if (isPlayableRef.current && audioCtxRef.current.state !== "closed") {
      bufferSourceRef.current.start(0);
    }
  }, [userSettings?.backgroundMusicVolume]);

  // Get sound file based on route and map mode
  const getSoundFileForRoute = useCallback((): string => {
    const pathname = window.location.pathname;
    
    // Check if we're on the home page (could be "/" or "/home")
    const isHomePage = pathname === "/" || pathname === "/home";
    
    // Play village music when:
    // 1. Map mode is enabled AND we're on home page, OR
    // 2. We're on the actual village route
    if ((isMapMode && isHomePage) || pathname.includes("/village")) {
      return "/sounds/village.winter.bgmc";
    }
    
    // For home page without map mode, use default music
    if (isHomePage) {
      return "/sounds/background-music.bgmc";
    }
    
    // For other pages, return empty string to not play music
    return "";
  }, [isMapMode]);

  // Initialize sound with proper error handling
  const initSound = useCallback(async () => {
    if (isInitializing.current || userSettings?.backgroundMusicVolume <= 0) return;
    
    isInitializing.current = true;
    
    try {
      const soundFile = getSoundFileForRoute();
      
      // If no sound file should be played, stop and return
      if (!soundFile) {
        stopCurrentAudio();
        isInitializing.current = false;
        return;
      }
      
      // Don't reload if it's the same sound file
      if (currentSoundFile.current === soundFile && AudioBufferState.current) {
        playSound(AudioBufferState.current);
        isInitializing.current = false;
        return;
      }
      
      currentSoundFile.current = soundFile;
      
      const response = await fetch(soundFile);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await audioCtxRef.current!.decodeAudioData(arrayBuffer);
      
      AudioBufferState.current = audioBuffer;
      playSound(audioBuffer);
      
    } catch (error) {
      console.error("Error loading background music:", error);
      
      // Fallback to default music if village music fails
      const defaultSoundFile = "/sounds/background-music.bgmc";
      if (currentSoundFile.current !== defaultSoundFile) {
        try {
          currentSoundFile.current = defaultSoundFile;
          const response = await fetch(defaultSoundFile);
          const arrayBuffer = await response.arrayBuffer();
          const audioBuffer = await audioCtxRef.current!.decodeAudioData(arrayBuffer);
          
          AudioBufferState.current = audioBuffer;
          playSound(audioBuffer);
        } catch (fallbackError) {
          console.error("Error loading fallback music:", fallbackError);
        }
      }
    } finally {
      isInitializing.current = false;
    }
  }, [getSoundFileForRoute, playSound, userSettings?.backgroundMusicVolume, stopCurrentAudio]);

  // Check if route is restricted
  const isRestrictedRoute = useCallback((pathname: string): boolean => {
    return (
      pathname.includes("replay") ||
      pathname.includes("/battle/order") ||
      pathname.includes("/arena/live") ||
      pathname.includes("/battle/treasure")
    );
  }, []);

  // Handle route detection
  const handleDetectRoute = useCallback(() => {
    const pathname = window.location.pathname;
    
    if (isRestrictedRoute(pathname)) {
      stopCurrentAudio();
    } else if (audioCtxRef.current) {
      if (audioCtxRef.current.state === "suspended") {
        try {
          const resumePromise = audioCtxRef.current.resume();
          if (resumePromise && resumePromise.catch) {
            resumePromise.catch(() => {
              console.error("Resume promise rejected; reinitializing audio context");
              initializeAudioContext();
            });
          }
        } catch (error) {
          console.error("Resume threw synchronously; reinitializing audio context");
          initializeAudioContext();
        }
      } else if (audioCtxRef.current.state === "closed") {
        initializeAudioContext();
      }
    }
  }, [isRestrictedRoute, stopCurrentAudio, initializeAudioContext]);

  // Handle stop
  const handleStop = useCallback(() => {
    if (audioCtxRef.current && audioCtxRef.current.state === "running") {
      try {
        audioCtxRef.current.suspend();
      } catch (e) {
        // Ignore errors if context is already closed
      }
    }
  }, []);

  // Handle resume
  const handleResume = useCallback(() => {
    if (audioCtxRef.current) {
      if (audioCtxRef.current.state === "suspended") {
        try {
          audioCtxRef.current.resume();
        } catch (e) {
          initializeAudioContext();
          setTimeout(() => {
            initSound();
          }, 100);
        }
      } else if (audioCtxRef.current.state === "closed") {
        initializeAudioContext();
        setTimeout(() => {
          initSound();
        }, 100);
      }
    }
  }, [initializeAudioContext, initSound]);

  // Switch music based on route or map mode changes
  const switchMusic = useCallback(async () => {
    if (isInitializing.current) return;
    
    const newRoute = window.location.pathname;
    const newSoundFile = getSoundFileForRoute();
    
    // Check if we need to switch music
    const routeChanged = currentRoute.current !== newRoute;
    const mapModeChanged = currentMapMode.current !== isMapMode;
    const soundFileChanged = currentSoundFile.current !== newSoundFile;
    
    if ((routeChanged || mapModeChanged || soundFileChanged)) {
      currentRoute.current = newRoute;
      currentMapMode.current = isMapMode;
      
      // Stop current audio first
      stopCurrentAudio();
      
      // If this is a restricted route or no sound file, don't start new audio
      if (isRestrictedRoute(newRoute) || !newSoundFile) {
        return;
      }
      
      // Always reinitialize audio context on switch
      initializeAudioContext();
      
      // Small delay to ensure context is ready
      setTimeout(() => {
        initSound();
      }, 100);
    }
  }, [getSoundFileForRoute, isMapMode, isRestrictedRoute, stopCurrentAudio, initializeAudioContext, initSound]);

  // Add user interaction detection
  useEffect(() => {
    const handleUserInteraction = () => {
      if (!hasUserInteracted.current) {
        hasUserInteracted.current = true;
        
        // Clear any existing timer
        if (initialDelayTimer.current) {
          clearTimeout(initialDelayTimer.current);
          initialDelayTimer.current = null;
        }
        
        // Start audio after user interaction with a small delay
        setTimeout(() => {
          if (hasUserInteracted.current) {
            initSound();
          }
        }, 500);
      }
    };

    // Listen for user interactions
    document.addEventListener('click', handleUserInteraction, { once: true });
    document.addEventListener('keydown', handleUserInteraction, { once: true });
    document.addEventListener('touchstart', handleUserInteraction, { once: true });

    return () => {
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
    };
  }, [initSound]);

  // Effect for playable state changes
  useEffect(() => {
    if (isPlayable) {
      handleDetectRoute();
    } else {
      stopCurrentAudio();
    }
  }, [isPlayable, handleDetectRoute, stopCurrentAudio]);

  // Effect for map mode changes - this is the key fix
  useEffect(() => {
    // Only switch music if we're not already initializing
    if (!isInitializing.current) {
      switchMusic();
    }
  }, [isMapMode, switchMusic]);

  // Effect for route changes
  useEffect(() => {
    const handlePopState = () => {
      setTimeout(() => {
        switchMusic();
      }, 100);
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [switchMusic]);

  // Pause and resume background music when the page becomes hidden or visible
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) {
        handleStop();
      } else {
        // Only resume if volume > 0 and route allows music
        if (userSettings?.backgroundMusicVolume <= 0) return;
        if (isRestrictedRoute(window.location.pathname)) return;
        const soundFile = getSoundFileForRoute();
        if (!soundFile) return;
        // If we've loaded audio, resume; otherwise initialize and play
        if (AudioBufferState.current) {
          handleResume();
        } else {
          initSound();
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [handleStop, handleResume, initSound, userSettings?.backgroundMusicVolume, getSoundFileForRoute, isRestrictedRoute]);

  // Initial setup
  useEffect(() => {
    if (!IsFirst.current) return;
    IsFirst.current = false;
    
    // Initialize audio context
    initializeAudioContext();
    
    // Set up 5-second delay fallback if user doesn't interact
    initialDelayTimer.current = setTimeout(() => {
      if (document.visibilityState === "visible" && !hasUserInteracted.current) {
        hasUserInteracted.current = true;
        initSound();
      }
    }, 5000);

    return () => {
      // Clear timer if it exists
      if (initialDelayTimer.current) {
        clearTimeout(initialDelayTimer.current);
        initialDelayTimer.current = null;
      }
      
      if (audioCtxRef.current) {
        // Only close if not already closed, and catch promise rejections
        if (audioCtxRef.current.state !== 'closed') {
          const closePromise = audioCtxRef.current.close();
          if (closePromise && typeof closePromise.catch === 'function') {
            closePromise.catch(() => {});
          }
        }
      }
    };
  }, [initializeAudioContext, initSound, handleStop, handleResume, isRestrictedRoute, userSettings?.backgroundMusicVolume]);

  return props.children;
};

export default SoundContainer;
